/**
 * PartnerProgramNote — shared, DYNAMIC user-facing copy about the Partner Program.
 *
 * Jul 30, 2026 (Randy pt89c-xxx) — Everywhere on the site that mentions
 * "you earn 25% referral commission" now reads THIS component. Result:
 *   - The base rev-share % is dynamic (from `affiliate_base_rev_share_pct`).
 *   - The tier-bump promise is added — after N successful referrals, the
 *     bonus jumps to the performance-tier rate. Both N and the perf-tier
 *     rate come from `platform_settings.thresholds` so moving the sliders
 *     updates every CTA, FAQ, and marketing card in one place.
 *
 * Randy explicitly asked for the tier-bump line to appear wherever the
 * 25% rev-share concept is broadcast (CreatorProBuffetModal, CreatorPro
 * GateModal, AffiliateUpgrade, AffiliateExplainer, FanPortal, FAQ...).
 *
 * Props:
 *   • variant — "full" (default) shows base + tier-bump line
 *               "compact" shows just base pct (older short copy)
 *   • className — passthrough for parent-controlled styling
 *   • intro — override the "Creator Pro members are also eligible…" lead-in
 *             (some spots use "you're also enrolled" for already-Pro users)
 */
import { usePublicPricing, fmtPct } from "@/lib/usePublicPricing";

const DEFAULT_INTRO =
  "Creator Pro members are also eligible for the ";

export default function PartnerProgramNote({
  variant = "full",
  className = "",
  intro = DEFAULT_INTRO,
  testId = "partner-program-note",
}) {
  const pricing = usePublicPricing();
  // Oct 3 2026 — these remembered 25 / 30 / 12. The live performance rate is
  // 33, so the remembered 30 understated a partner's own earnings on the one
  // note whose whole job is explaining the rate. Unknown now renders "—".
  const basePct = fmtPct(pricing?.affiliate_base_rev_share_pct);
  const perfPct = fmtPct(pricing?.affiliate_performance_rev_share_pct);
  const perfThreshold = pricing?.affiliate_performance_active_customers_threshold;

  return (
    <span data-testid={testId} className={className}>
      {intro}
      <strong>My Silo Partner Program</strong> — earn a <strong>{basePct}</strong> referral
      commission on Creator Pro software you help sell.
      {variant === "full" && perfThreshold != null && (
        <>
          {" "}After <strong>{perfThreshold}</strong> successful referrals, your bonus jumps
          from {basePct} to <strong>{perfPct}</strong>.
        </>
      )}
    </span>
  );
}
